import React, { useState, useEffect, useCallback, useRef, useMemo, useContext } from 'react';
import { BrandingContext } from '../../../../BrandingProvider';
import { Box, Grid, Stepper, Step, StepLabel, Typography, Table, TableHead,
  TableRow, TableCell, TableBody, Alert, CircularProgress, Divider, useTheme, useMediaQuery } from '@mui/material';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import AddIcon from '@mui/icons-material/Add';
import PictureAsPdfIcon from '@mui/icons-material/PictureAsPdf';
import VisibilityIcon from '@mui/icons-material/Visibility';
import DownloadIcon from '@mui/icons-material/Download';
import { useHistory, useLocation, useRouteMatch } from 'react-router-dom';
import { toast } from 'react-toastify';
import SectionCard from '../../components/SectionCard';
import InvButton from '../../components/InvButton';
import { InvTextField, InvSelect } from '../../components/InvField';
import { getPurchaseOrders, getPurchaseOrderItems, getPurchaseOrder } from '../../api/procurement';
import { getPurchasableVendors } from '../../api/vendor';
import useItemNames from '../../hooks/useItemNames';
import { postToStock } from '../../api/grn';
import { getStores } from '../../api/stockLedger';
import { getUoms } from '../../api/inventoryMaster';
import useVendorLookup from '../../hooks/useVendorLookup';
import { generateTxnNumber, formatDate, formatQty, formatCurrency,
  sanitizeQtyInput, parseWholeQty, parsePositiveRate, computeGrnFinancials } from '../../utils';
import { resolveItemDisplayName, meaningfulItemName, itemOptionLabel, enrichLineWithItemTracking, defaultUom } from '../../utils/itemDisplay';
import { normalizeUomList, toSelectOptions } from '../../utils/masterEntity';
import useItemAllowedUoms from '../../hooks/useItemAllowedUoms';
import {
  defaultPackToIssue, formatConversionFactor, parseConversionFactor,
} from '../../utils/uomConversionEntity';
import GrnReceiptModeSelector from '../../components/material/GrnReceiptModeSelector';
import GrnLineItemsEditor from '../../components/material/GrnLineItemsEditor';
import { getPoItemId } from '../../components/PoItemAutocomplete';
import { buildGrnPdfPackage, openGrnPdfUrl, downloadGrnPdfUrl } from '../../utils/grnPdf';
import LetterheadMenu, { useLetterheadMenu } from '../../components/LetterheadMenu';
import { canReceiveGrn } from '../../utils/storeEntity';
import {
  todayGrnDate,
  validateGrnDetails,
  validateDirectGrnDetails,
  validateDirectGrnLines,
  validatePoGrnLines,
  getDirectLineValidationErrors,
  getPoLineValidationErrors,
  parseGrnApiLineError,
  DIRECT_GRN_LINE_DEFAULT,
  PO_GRN_LINE_DEFAULT,
  extractGrnScope,
  resolveDirectGrnScope,
  mapPoItemToWizardLine,
  mapGrnItemToWizardLine,
  normalizeGrnStatus,
  canPostGrnToStock,
  isGrnPosted,
  syncLineReceivedQty,
  syncLineAcceptQty,
  syncLineAcceptFreeQty,
  resolveLineAcceptFreeQty,
  validateWizardLineQc,
  validateGrnLinesForCreate,
  isGrnWizardLinesValid,
  canOverrideGrnPricing,
  getGrnHeaderDateFieldErrors,
} from '../../utils/grnEntity';
import { normalizeToIsoDate } from '../../../ipd/utils/dateFormat';
import { submitPoGrnReceipt, submitDirectGrnReceipt, applyGrnQualityCheck, refreshGrnWorkflowState } from '../../utils/grnWorkflow';
import { GRN_ELIGIBLE_PO_STATUSES, isPoEligibleForGrn } from '../../utils/poLifecycle';
import { GRN_KINDS, GRN_PAYMENT_TYPES, GRN_MODES, GRN_STEP_LABELS } from '../../constants/grnManagement';
import useInvRole from '../../hooks/useInvRole';
import { INV_ACTIONS } from '../../utils/inventoryRoles';
import { IPD_COLORS, cardSx } from '../../theme';

function resolveInitialGrnMode(preselectedPo, searchParams) {
  if (preselectedPo) return GRN_MODES.PO;
  if (searchParams.get('mode') === 'direct') return GRN_MODES.DIRECT;
  return GRN_MODES.PO;
}

function focusGrnField(field) {
  const anchor = document.getElementById(`grn-field-${field}`);
  if (!anchor) return;
  anchor.scrollIntoView({ behavior: 'smooth', block: 'center' });
  const focusable = anchor.querySelector(
    'input, textarea, [role="combobox"], .MuiSelect-select',
  );
  if (focusable) setTimeout(() => focusable.focus(), 200);
}

function FormSection({ title }) {
  return (
    <Grid item xs={12}>
      <Typography
        variant="overline"
        sx={{ color: IPD_COLORS.textSecondary, fontSize: 11, letterSpacing: 0.8, display: 'block', mt: 1 }}
      >
        {title}
      </Typography>
      <Divider sx={{ mb: 0.5, borderColor: IPD_COLORS.border }} />
    </Grid>
  );
}

function poRowId(po) {
  return po?.purchase_order_id || po?.id;
}

function mergePurchaseOrders(...lists) {
  const seen = new Map();
  for (const po of lists.flat()) {
    const id = poRowId(po);
    if (id && !seen.has(id)) seen.set(id, po);
  }
  return [...seen.values()];
}

function grnFormFromPo(po, prev) {
  return {
    ...prev,
    vendor_id: po.vendor_id || '',
    external_po_reference: po.external_po_reference || po.vendor_po_number || prev.external_po_reference,
    place_of_supply: po.place_of_supply || po.state || prev.place_of_supply,
  };
}

function normalizeList(data) {
  return Array.isArray(data) ? data : (data?.items || data?.data || []);
}

function displayItemName(item, catalog, wizardLines = []) {
  const itemId = item.item_id;
  const fromLine = wizardLines.find(l => l.item_id === itemId);
  const lineName = meaningfulItemName(fromLine?.item_name, itemId);
  const apiName = meaningfulItemName(item.item_name, itemId);
  return resolveItemDisplayName(
    { ...item, item_name: lineName || apiName || item.item_name },
    catalog,
  );
}

function buildUomMap(uoms) {
  const map = {};
  (Array.isArray(uoms) ? uoms : []).forEach((u) => {
    const id = u.uom_id || u.id;
    if (id) map[id] = u.uom_code || u.uom_name || u.name || '';
  });
  return map;
}

function GrnFinancialFooter({ financials, useAccepted }) {
  const { subtotal, totalTax, taxBuckets, grandTotal } = financials;
  const taxEntries = Object.entries(taxBuckets).sort(([a], [b]) => Number(a) - Number(b));

  return (
    <Box sx={{
      display: 'flex', justifyContent: 'flex-end', mt: 2, pt: 1.5,
      borderTop: `1px solid ${IPD_COLORS.border}`,
    }}>
      <Box sx={{ minWidth: 280 }}>
        {useAccepted && (
          <Typography sx={{ color: IPD_COLORS.textSecondary, fontSize: 11, mb: 1, textAlign: 'right' }}>
            Based on accepted quantities after QC
          </Typography>
        )}
        <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
          <Typography sx={{ color: IPD_COLORS.textSecondary, fontSize: 13 }}>Subtotal</Typography>
          <Typography sx={{ fontFamily: 'monospace', fontSize: 13 }}>{formatCurrency(subtotal)}</Typography>
        </Box>
        {taxEntries.map(([rate, amt]) => (
          <Box key={rate} sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
            <Typography sx={{ color: IPD_COLORS.textSecondary, fontSize: 13 }}>GST ({rate}%)</Typography>
            <Typography sx={{ fontFamily: 'monospace', fontSize: 13 }}>{formatCurrency(amt)}</Typography>
          </Box>
        ))}
        {!taxEntries.length && totalTax > 0 && (
          <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
            <Typography sx={{ color: IPD_COLORS.textSecondary, fontSize: 13 }}>Tax</Typography>
            <Typography sx={{ fontFamily: 'monospace', fontSize: 13 }}>{formatCurrency(totalTax)}</Typography>
          </Box>
        )}
        <Box sx={{
          display: 'flex', justifyContent: 'space-between', mt: 1, pt: 1,
          borderTop: `1px solid ${IPD_COLORS.border}`,
        }}>
          <Typography sx={{ color: IPD_COLORS.textPrimary, fontWeight: 700, fontSize: 15 }}>Grand Total</Typography>
          <Typography sx={{ color: IPD_COLORS.primary, fontWeight: 700, fontSize: 15, fontFamily: 'monospace' }}>
            {formatCurrency(grandTotal)}
          </Typography>
        </Box>
      </Box>
    </Box>
  );
}

function GrnDetailsFields({
  grnData, formErrors, isDirect, grnDateToday, vendorOptions, vendorsLoading,
  storeOptions, updateGrnField, onVendorSelect, resolveVendorName, storeLocked,
}) {
  return (
    <Grid container spacing={2} sx={{ maxWidth: { xs: '100%', lg: 960 } }}>
      <FormSection title="Document" />
      <Grid item xs={12} sm={6} md={4}>
        <InvTextField label="GRN Number" value={grnData.grn_number}
          onChange={e => updateGrnField('grn_number', e.target.value)} />
      </Grid>
      <Grid item xs={12} sm={6} md={4} id="grn-field-grn_date">
        <InvTextField
          name="grn_date"
          label="GRN Date"
          type="date"
          InputLabelProps={{ shrink: true }}
          value={grnData.grn_date || grnDateToday}
          onChange={e => updateGrnField('grn_date', e.target.value)}
          error={Boolean(formErrors.grn_date)}
          helperText={formErrors.grn_date || 'Defaults to today; you can change if needed'}
        />
      </Grid>
      <Grid item xs={12} sm={6} md={4}>
        {isDirect ? (
          <InvTextField label="GRN Kind" value="Direct Receipt" disabled />
        ) : (
          <InvSelect label="GRN Kind" value={grnData.grn_kind}
            onChange={e => updateGrnField('grn_kind', e.target.value)}
            options={GRN_KINDS} />
        )}
      </Grid>

      <FormSection title={isDirect ? 'Vendor' : 'Purchase & Vendor'} />
      <Grid item xs={12} sm={6} md={4} id="grn-field-vendor_id">
        {isDirect ? (
          <InvSelect
            name="vendor_id"
            label="Vendor *"
            value={grnData.vendor_id}
            onChange={e => onVendorSelect(e.target.value)}
            options={vendorOptions}
            disabled={vendorsLoading}
            error={Boolean(formErrors.vendor_id)}
            helperText={formErrors.vendor_id}
          />
        ) : (
          <InvTextField label="Vendor" value={resolveVendorName(grnData.vendor_id) || '—'} disabled />
        )}
      </Grid>
      <Grid item xs={12} sm={6} md={4}>
        <InvTextField label="External PO Reference" value={grnData.external_po_reference}
          onChange={e => updateGrnField('external_po_reference', e.target.value)}
          placeholder={isDirect ? 'Optional vendor reference' : 'Vendor PO / reference number'} />
      </Grid>

      <FormSection title="Invoice & Payment" />
      <Grid item xs={12} sm={6} md={4} id="grn-field-invoice_number">
        <InvTextField
          name="invoice_number"
          label="Invoice Number *"
          value={grnData.invoice_number}
          onChange={e => updateGrnField('invoice_number', e.target.value)}
          error={Boolean(formErrors.invoice_number)}
          helperText={formErrors.invoice_number}
        />
      </Grid>
      <Grid item xs={12} sm={6} md={4} id="grn-field-invoice_date">
        {/*
          Calendar min/max alone are not enough: clear + keyboard type can bypass
          the popup range. IpdDateField normalizes to yyyy-MM-dd; live errors come
          from getGrnHeaderDateFieldErrors via updateGrnField.
        */}
        <InvTextField
          name="invoice_date"
          label="Invoice Date *"
          type="date"
          InputLabelProps={{ shrink: true }}
          value={grnData.invoice_date || ''}
          onChange={e => updateGrnField('invoice_date', e.target.value)}
          inputProps={{ max: grnData.delivery_date || grnDateToday }}
          error={Boolean(formErrors.invoice_date)}
          helperText={formErrors.invoice_date}
        />
      </Grid>
      <Grid item xs={12} sm={6} md={4}>
        <InvSelect label="Payment Type" value={grnData.payment_type}
          onChange={e => updateGrnField('payment_type', e.target.value)}
          options={GRN_PAYMENT_TYPES} />
      </Grid>
      <Grid item xs={12} sm={6} md={4} id="grn-field-place_of_supply">
        <InvTextField
          name="place_of_supply"
          label="Place of Supply"
          value={grnData.place_of_supply}
          onChange={e => updateGrnField('place_of_supply', e.target.value)}
          placeholder="e.g. Maharashtra"
        />
      </Grid>

      <FormSection title="Delivery" />
      <Grid item xs={12} sm={6} md={4}>
        <InvTextField label="Delivery Challan " value={grnData.delivery_challan_number}
          onChange={e => updateGrnField('delivery_challan_number', e.target.value)} />
      </Grid>
      <Grid item xs={12} sm={6} md={4} id="grn-field-delivery_date">
        {/* Same silent calendar bug as invoice_date — see comment above. */}
        <InvTextField
          name="delivery_date"
          label="Delivery Date"
          type="date"
          InputLabelProps={{ shrink: true }}
          value={grnData.delivery_date || ''}
          onChange={e => updateGrnField('delivery_date', e.target.value)}
          inputProps={{ min: grnData.invoice_date || undefined, max: grnDateToday }}
          error={Boolean(formErrors.delivery_date)}
          helperText={formErrors.delivery_date}
        />
      </Grid>

      <FormSection title="Receiving" />
      <Grid item xs={12} sm={6} md={4} id="grn-field-receiving_store_id">
        <InvSelect
          name="receiving_store_id"
          label="Store *"
          value={grnData.receiving_store_id}
          onChange={e => updateGrnField('receiving_store_id', e.target.value)}
          options={storeOptions}
          error={Boolean(formErrors.receiving_store_id)}
          helperText={formErrors.receiving_store_id}
          disabled={storeLocked}
        />
      </Grid>
      <Grid item xs={12}>
        <InvTextField label="Remarks" multiline rows={2} value={grnData.remarks}
          onChange={e => updateGrnField('remarks', e.target.value)} />
      </Grid>
    </Grid>
  );
}

export default function GrnWizard() {
  const history = useHistory();
  const { url } = useRouteMatch();
  const theme = useTheme();
  const isCompactStepper = useMediaQuery(theme.breakpoints.down('md'));
  const { catalog } = useItemNames();
  const { resolveVendorName } = useVendorLookup();
  const materialBase = url.replace('/grn/new', '');
  const brandingData = useContext(BrandingContext);
  const facilityLogo = (() => {
    const img = brandingData?.find(b => b.name === 'collateral_image')?.content;
    return img ? `data:image/png;base64,${img}` : null;
  })();
  const { can, myStoreId, myStoreIds } = useInvRole();
  const location = useLocation();
  const params = new URLSearchParams(location.search);
  const preselectedPo = params.get('po');
  const resumeGrnId = params.get('grn_id');
  const isPoLocked = Boolean(preselectedPo);
  const initialGrnMode = resolveInitialGrnMode(preselectedPo, params);

  const [grnMode, setGrnMode] = useState(initialGrnMode);
  const isDirect = grnMode === GRN_MODES.DIRECT;
  const stepLabels = GRN_STEP_LABELS[grnMode];

  const [step, setStep] = useState(0);
  const [loading, setLoading] = useState(false);
  const [createProgress, setCreateProgress] = useState('');
  const [posted, setPosted] = useState(false);
  const [grnStatus, setGrnStatus] = useState(null);

  const [pos, setPos] = useState([]);
  const [selectedPoId, setSelectedPoId] = useState(preselectedPo || '');
  const [selectedPo, setSelectedPo] = useState(null);

  const [purchasableVendors, setPurchasableVendors] = useState([]);
  const [vendorsLoading, setVendorsLoading] = useState(false);

  const [stores, setStores] = useState([]);
  const [uomMap, setUomMap] = useState({});
  const [uoms, setUoms] = useState([]);
  const [grnScope, setGrnScope] = useState({});
  const [grnData, setGrnData] = useState(() => ({
    grn_number: generateTxnNumber(initialGrnMode === GRN_MODES.DIRECT ? 'GRN-DIRECT' : 'GRN'),
    grn_kind: initialGrnMode === GRN_MODES.DIRECT ? 'DIRECT' : 'STANDARD',
    grn_date: todayGrnDate(),
    vendor_id: '',
    receiving_store_id: '',
    invoice_number: '',
    invoice_date: '',
    delivery_challan_number: '',
    delivery_date: '',
    payment_type: 'CREDIT',
    place_of_supply: '',
    external_po_reference: '',
    gate_entry_number: '',
    gate_entry_date: '',
    remarks: '',
  }));
  const [formErrors, setFormErrors] = useState({});
  const [grnId, setGrnId] = useState(null);
  const [lines, setLines] = useState([]);
  const [poItems, setPoItems] = useState([]);
  const [grnItems, setGrnItems] = useState([]);
  const [linesLoading, setLinesLoading] = useState(false);
  const [linesLoadError, setLinesLoadError] = useState(null);
  const [submitLineError, setSubmitLineError] = useState(null);
  const [lineErrorOverrides, setLineErrorOverrides] = useState({});
  const [linesValidationAttempted, setLinesValidationAttempted] = useState(false);
  const poLinesFetchPoRef = useRef(null);
  const poLinesAttemptRef = useRef(null);

  const [postResult, setPostResult] = useState(null);
  const [pdfLoading, setPdfLoading] = useState(false);
  const letterheadMenu = useLetterheadMenu();
  const [pdfPackage, setPdfPackage] = useState(null);
  const pdfUrlRef = useRef(null);
  const resumeAttemptRef = useRef(null);

  const qcCompleted = canPostGrnToStock(grnStatus);

  useEffect(() => {
    if (!isPoLocked) {
      setVendorsLoading(true);
      getPurchasableVendors()
        .then(d => setPurchasableVendors(normalizeList(d)))
        .catch(() => setPurchasableVendors([]))
        .finally(() => setVendorsLoading(false));
    }
    Promise.all(
      GRN_ELIGIBLE_PO_STATUSES.map(status => getPurchaseOrders({ status })),
    )
      .then((results) => {
        const all = mergePurchaseOrders(...results.map(normalizeList));
        setPos(all);
        if (!preselectedPo) return;
        const found = all.find(p => poRowId(p) === preselectedPo);
        if (found) {
          setSelectedPo(found);
          setGrnData(g => grnFormFromPo(found, g));
          return;
        }
        getPurchaseOrder(preselectedPo)
          .then((po) => {
            if (!isPoEligibleForGrn(po)) return;
            setSelectedPo(po);
            setPos(prev => mergePurchaseOrders(prev, [po]));
            setGrnData(g => grnFormFromPo(po, g));
          })
          .catch(() => {});
      })
      .catch(() => {});
    getStores().then((d) => {
      const list = normalizeList(d);
      setStores(list);
      // Store-scoped users receive into their own store. Only when the field is
      // still empty, so resuming an existing GRN (hydrateFromGrn) is not clobbered.
      // Guarded on membership: storeOptions is canReceiveGrn-filtered, so a
      // child-store user's own store is legitimately absent — leave it open then.
      const mine = myStoreId && list.find(
        x => canReceiveGrn(x) && (x.store_id || x.id) === myStoreId,
      );
      if (mine) {
        setGrnData((g) => {
          if (g.receiving_store_id) return g;
          // Mirrors updateGrnField: a direct GRN cannot be submitted without the
          // store's tenant/hospital scope. Harmless in PO mode — toPoGrnPayload
          // takes scope from the purchase order, not from here.
          const next = { ...g, receiving_store_id: myStoreId };
          if (mine.tenant_id) next.tenant_id = mine.tenant_id;
          if (mine.hospital_id) next.hospital_id = mine.hospital_id;
          return next;
        });
      }
    }).catch(() => {});
    getUoms({ is_active: true, limit: 200 })
      .then(d => {
        const list = normalizeList(d);
        setUomMap(buildUomMap(list));
        setUoms(normalizeUomList(list));
      })
      .catch(() => { setUomMap({}); setUoms([]); });
  }, [preselectedPo, myStoreId]);

  const uomOptions = useMemo(() => toSelectOptions(uoms, {
    idKey: 'uom_id', nameKey: 'uom_name', codeKey: 'uom_code',
  }), [uoms]);

  // Both modes need this now. The UOM cell is read-only everywhere, but every
  // line still needs the item's rules to prefill its pack size and to bridge
  // the dispense unit to the base unit — and a PO line has the same pack size
  // question as a Direct one.
  const baseUomByItem = useMemo(() => {
    const map = {};
    lines.forEach((l) => {
      if (!l.item_id || map[l.item_id]) return;
      const baseUomId = l.base_uom_id || catalog?.byId?.get(l.item_id)?.base_uom_id;
      if (baseUomId) map[l.item_id] = baseUomId;
    });
    return map;
  }, [lines, catalog]);

  const uomPolicy = useItemAllowedUoms({
    baseUomByItem,
    uomOptions,
    enabled: true,
  });

  const { factorFor } = uomPolicy;

  // Primitive key, not the lines array: the effect below writes to lines, so
  // depending on the array itself would re-fire on every keystroke. Only the
  // item and the purchase UOM change what a line's pack size resolves to.
  const lineUomKey = lines.map(l => `${l.item_id}:${l.uom_id}`).join(',');

  // Resolve each line's pack size against the item master.
  //
  // Three things land on the line here, and all three need the item's
  // conversion rules, which arrive asynchronously — so this re-runs when
  // factorFor changes identity rather than when the line is first created:
  //
  //   issue_to_base_factor   the bridge the payload composes through
  //   pack_to_issue_default  what the master says, so "changed?" can be asked
  //   pack_to_issue          seeded from the default, once
  //
  // It is also the only moment a resumed line's saved override — stored in base
  // units — can be divided back into the dispense-unit number that was typed.
  useEffect(() => {
    setLines((prev) => {
      let changed = false;
      const next = prev.map((line) => {
        if (!line.item_id) return line;
        const master = catalog?.byId?.get(line.item_id);
        const baseUomId = line.base_uom_id || master?.base_uom_id || '';
        const issueUomId = line.issue_uom_id
          || (master ? defaultUom(master, 'issue') : '') || '';
        // Dispense unit IS the base unit for all but sixteen items; there is
        // nothing to bridge and the typed number is already what posts.
        const issueToBase = !issueUomId || String(issueUomId) === String(baseUomId)
          ? 1
          : factorFor(line.item_id, issueUomId);
        const defaultPack = defaultPackToIssue(
          factorFor(line.item_id, line.uom_id),
          issueToBase,
        );
        const defaultStr = defaultPack == null ? '' : formatConversionFactor(defaultPack);

        const patch = {};
        if (baseUomId !== line.base_uom_id) patch.base_uom_id = baseUomId;
        if (issueUomId !== line.issue_uom_id) patch.issue_uom_id = issueUomId;
        const bridgeStr = issueToBase == null ? '' : String(issueToBase);
        if (bridgeStr !== line.issue_to_base_factor) patch.issue_to_base_factor = bridgeStr;
        if (defaultStr !== line.pack_to_issue_default) patch.pack_to_issue_default = defaultStr;

        const savedBase = parseConversionFactor(line.pack_override_base);
        if (savedBase != null && issueToBase != null) {
          patch.pack_to_issue = formatConversionFactor(savedBase / issueToBase);
          patch.pack_override_base = '';
        } else if (!line.pack_to_issue && !line.pack_to_issue_default && defaultStr) {
          // Seeded only on the transition from "master unknown" to "master
          // known", so clearing the field by hand stays cleared.
          patch.pack_to_issue = defaultStr;
        }

        if (!Object.keys(patch).length) return line;
        changed = true;
        return { ...line, ...patch };
      });
      return changed ? next : prev;
    });
  }, [factorFor, catalog, lineUomKey]);

  const hydrateFromGrn = useCallback(async (grn, items) => {
    const status = normalizeGrnStatus(grn.status);
    const scope = extractGrnScope(grn);
    const poId = grn.purchase_order_id;

    if (poId) {
      setGrnMode(GRN_MODES.PO);
      setSelectedPoId(poId);
      try {
        const po = await getPurchaseOrder(poId);
        setSelectedPo(po);
      } catch {
        setSelectedPo(pos.find(p => (p.purchase_order_id || p.id) === poId) || null);
      }
    } else if (grn.grn_kind === 'DIRECT') {
      setGrnMode(GRN_MODES.DIRECT);
    }

    setGrnId(grn.grn_id || grn.id);
    setGrnScope(scope);
    setGrnStatus(status);
    setGrnData(g => ({
      ...g,
      grn_number: grn.grn_number || g.grn_number,
      grn_kind: grn.grn_kind || g.grn_kind,
      grn_date: grn.grn_date || g.grn_date,
      vendor_id: grn.vendor_id || g.vendor_id,
      receiving_store_id: grn.receiving_store_id || g.receiving_store_id,
      invoice_number: grn.invoice_number || g.invoice_number,
      invoice_date: grn.invoice_date || g.invoice_date,
      delivery_challan_number: grn.delivery_challan_number || g.delivery_challan_number,
      delivery_date: grn.delivery_date || g.delivery_date,
      payment_type: grn.payment_type || g.payment_type,
      place_of_supply: grn.place_of_supply || g.place_of_supply,
      external_po_reference: grn.external_po_reference || g.external_po_reference,
      gate_entry_number: grn.gate_entry_number || g.gate_entry_number,
      gate_entry_date: grn.gate_entry_date || g.gate_entry_date,
      remarks: grn.remarks || g.remarks,
    }));
    setGrnItems(items);
    setLines(items.map(item => mapGrnItemToWizardLine(
      item,
      meaningfulItemName(item.item_name, item.item_id),
      catalog,
    )));

    if (isGrnPosted(status, grn.posting_status)) {
      setPosted(true);
      setStep(5);
    } else if (canPostGrnToStock(status)) {
      // QC already completed — go straight to Post to Stock.
      setStep(5);
    } else if (grn.grn_id || grn.id) {
      // QC still pending — resume at the QC step so accept/reject can be set.
      setStep(3);
    }
  }, [catalog, pos]);

  useEffect(() => {
    if (!resumeGrnId || resumeAttemptRef.current === resumeGrnId) return;
    resumeAttemptRef.current = resumeGrnId;
    setLoading(true);
    refreshGrnWorkflowState(resumeGrnId)
      .then(({ grn, items }) => hydrateFromGrn(grn, items))
      .catch((e) => {
        toast.error(e?.message || 'Failed to load GRN for resume');
        resumeAttemptRef.current = null;
      })
      .finally(() => setLoading(false));
  }, [resumeGrnId, hydrateFromGrn]);

  useEffect(() => {
    if (!catalog) return;
    setLines(prev => {
      if (!prev.length) return prev;
      const next = prev.map(line => enrichLineWithItemTracking(line, catalog));
      const changed = next.some((line, i) =>
        line.is_batch_tracked !== prev[i].is_batch_tracked
        || line.is_expiry_tracked !== prev[i].is_expiry_tracked
        || line.item_code !== prev[i].item_code,
      );
      return changed ? next : prev;
    });
  }, [catalog]);

  function onPoSelect(poId) {
    setSelectedPoId(poId);
    const found = pos.find(p => poRowId(p) === poId);
    setSelectedPo(found || null);
    setLines([]);
    setPoItems([]);
    setLinesLoadError(null);
    poLinesFetchPoRef.current = null;
    poLinesAttemptRef.current = null;
    setGrnData(g => (found ? grnFormFromPo(found, g) : {
      ...g,
      vendor_id: '',
      external_po_reference: g.external_po_reference,
      place_of_supply: g.place_of_supply,
    }));
  }

  function onVendorSelect(vendorId) {
    const found = purchasableVendors.find(v => (v.vendor_id || v.id) === vendorId);
    setGrnData(g => ({
      ...g,
      vendor_id: vendorId,
      place_of_supply: found?.state || g.place_of_supply,
      tenant_id: g.tenant_id || found?.tenant_id,
      hospital_id: g.hospital_id || found?.hospital_id,
    }));
    setFormErrors(prev => {
      if (!prev.vendor_id) return prev;
      const next = { ...prev };
      delete next.vendor_id;
      return next;
    });
  }

  function handleReceiptModeChange(mode) {
    if (mode === grnMode || isPoLocked) return;
    setGrnMode(mode);
    setSelectedPoId('');
    setSelectedPo(null);
    setLines([]);
    setPoItems([]);
    setGrnId(null);
    setGrnItems([]);
    setLinesLoadError(null);
    poLinesFetchPoRef.current = null;
    poLinesAttemptRef.current = null;
    setGrnScope({});
    setGrnStatus(null);
    setFormErrors({});
    setPosted(false);
    setStep(0);
    if (mode === GRN_MODES.DIRECT) {
      setGrnData(g => ({
        ...g,
        grn_kind: 'DIRECT',
        grn_number: generateTxnNumber('GRN-DIRECT'),
        vendor_id: '',
        external_po_reference: '',
      }));
    } else {
      setGrnData(g => ({
        ...g,
        grn_kind: 'STANDARD',
        grn_number: generateTxnNumber('GRN'),
        vendor_id: '',
        gate_entry_number: '',
        gate_entry_date: '',
      }));
    }
  }

  function updateGrnField(key, value) {
    const dateKeys = ['invoice_date', 'delivery_date', 'grn_date', 'gate_entry_date'];
    const nextValue = dateKeys.includes(key) ? (normalizeToIsoDate(value) || value) : value;

    setGrnData((g) => {
      const next = { ...g, [key]: nextValue };
      if (key === 'receiving_store_id' && isDirect) {
        const store = stores.find(s => String(s.store_id || s.id) === String(value));
        if (store?.tenant_id) next.tenant_id = store.tenant_id;
        if (store?.hospital_id) next.hospital_id = store.hospital_id;
      }

      if (key === 'invoice_date' || key === 'delivery_date') {
        const dateErrs = getGrnHeaderDateFieldErrors(next);
        setFormErrors((prev) => {
          const out = { ...prev };
          delete out.invoice_date;
          delete out.delivery_date;
          return { ...out, ...dateErrs };
        });
      } else {
        setFormErrors((prev) => {
          if (!prev[key]) return prev;
          const out = { ...prev };
          delete out[key];
          return out;
        });
      }
      return next;
    });
  }

  function directScope() {
    return resolveDirectGrnScope({
      grnData,
      stores,
      vendors: purchasableVendors,
    });
  }

  function poScope() {
    return {
      tenant_id: selectedPo?.tenant_id,
      hospital_id: selectedPo?.hospital_id,
    };
  }

  function workflowScope() {
    if (!isDirect) return poScope();
    return grnScope;
  }

  function rememberGrnFromResponse(grn) {
    setGrnId(grn.grn_id || grn.id);
    setGrnScope(extractGrnScope(grn));
    if (grn.status) {
      setGrnStatus(normalizeGrnStatus(grn.status));
    }
    if (grn.grn_number) {
      setGrnData(g => ({ ...g, grn_number: grn.grn_number }));
    }
  }

  const loadPoItems = useCallback(async () => {
    if (isDirect || !selectedPoId) return;

    setLinesLoading(true);
    setLinesLoadError(null);
    try {
      const items = await getPurchaseOrderItems(selectedPoId, poScope());
      const arr = normalizeList(items);
      if (!arr.length) {
        const msg = 'This purchase order has no line items';
        setLinesLoadError(msg);
        toast.error(msg);
        return;
      }
      setPoItems(arr.map(item => ({
        ...item,
        item_name: displayItemName(item, catalog) || item.item_name,
      })));
      poLinesFetchPoRef.current = selectedPoId;
    } catch (e) {
      const msg = e?.message || 'Failed to load purchase order items';
      setLinesLoadError(msg);
      toast.error(msg);
    } finally {
      setLinesLoading(false);
    }
  }, [isDirect, selectedPoId, catalog, selectedPo]);

  useEffect(() => {
    if (step < 2) {
      poLinesAttemptRef.current = null;
    }
  }, [step]);

  useEffect(() => {
    if (step !== 2 || posted) return;

    if (isDirect) {
      setLines(prev => (prev.length ? prev : [{ ...DIRECT_GRN_LINE_DEFAULT }]));
      return;
    }

    setLines(prev => (prev.length ? prev : [{ ...PO_GRN_LINE_DEFAULT }]));

    if (poItems.length > 0 || linesLoading) return;
    if (poLinesAttemptRef.current === selectedPoId) return;

    poLinesAttemptRef.current = selectedPoId;
    loadPoItems();
  }, [step, posted, isDirect, poItems.length, linesLoading, selectedPoId, loadPoItems]);

  function proceedFromSource() {
    setStep(1);
  }

  function validateDetailsStep() {
    const validationError = isDirect
      ? validateDirectGrnDetails(grnData)
      : validateGrnDetails(grnData);
    if (validationError) {
      setFormErrors({ [validationError.field]: validationError.message });
      toast.error(validationError.message);
      focusGrnField(validationError.field);
      return false;
    }
    setFormErrors({});
    return true;
  }

  function proceedFromDetails() {
    if (!validateDetailsStep()) return;
    setLinesLoadError(null);
    setLinesValidationAttempted(false);
    setSubmitLineError(null);
    setStep(2);
  }

  function proceedFromItems() {
    setLinesValidationAttempted(true);
    const lineErr = validateAllLines();
    if (lineErr) {
      setSubmitLineError(lineErr);
      toast.error(lineErr);
      return;
    }
    setSubmitLineError(null);
    setLineErrorOverrides({});
    setStep(3);
  }

  function insertLine(defaultLine, afterIndex) {
    setLines(prev => {
      const newLine = { ...defaultLine };
      if (afterIndex === undefined || afterIndex < 0) return [...prev, newLine];
      const next = [...prev];
      next.splice(afterIndex + 1, 0, newLine);
      return next;
    });
  }

  function addDirectLine(afterIndex) {
    insertLine(DIRECT_GRN_LINE_DEFAULT, afterIndex);
  }

  function removeDirectLine(index) {
    setLines(prev => (prev.length <= 1 ? prev : prev.filter((_, idx) => idx !== index)));
  }

  function canAddPoLine() {
    return poItems.length > 0 && lines.length < poItems.length;
  }

  function addPoLine(afterIndex) {
    if (!canAddPoLine()) {
      toast.error('All purchase order items have already been added');
      return;
    }
    insertLine(PO_GRN_LINE_DEFAULT, afterIndex);
  }

  function tryAddLine(afterIndex) {
    if (isDirect) {
      addDirectLine(afterIndex);
      return;
    }
    addPoLine(afterIndex);
  }

  function removePoLine(index) {
    setLines(prev => (prev.length <= 1 ? prev : prev.filter((_, idx) => idx !== index)));
  }

  function availablePoItemsForRow(rowIndex) {
    const selectedIds = new Set(
      lines
        .filter((_, idx) => idx !== rowIndex)
        .map(line => line.po_item_id || line.purchase_order_item_id)
        .filter(Boolean),
    );
    return poItems.filter(item => !selectedIds.has(getPoItemId(item)));
  }

  function updatePoLine(index, key, val, poItem) {
    setLines(prev => prev.map((row, idx) => {
      if (idx !== index) return row;
      if (key === 'po_item') {
        if (!poItem) return { ...PO_GRN_LINE_DEFAULT };
        return mapPoItemToWizardLine(
          poItem,
          poItem.item_name || displayItemName(poItem, catalog),
          catalog,
        );
      }
      let updated = { ...row, [key]: val };
      if (key === 'received_quantity') {
        updated = syncLineReceivedQty(updated, sanitizeQtyInput(val));
      } else if (key === 'unit_rate' || key === 'discount_percent') {
        const received = Math.floor(Number(updated.received_quantity) || 0);
        updated.discount_amount = String(
          (received * (Number(updated.unit_rate) || 0) * (Number(updated.discount_percent) || 0)) / 100,
        );
        if (key === 'unit_rate' && updated.po_unit_rate !== ''
          && Math.abs(Number(updated.unit_rate) - Number(updated.po_unit_rate)) <= 0.001) {
          updated.rate_override_reason = '';
        }
      }
      if (key === 'accept_quantity') {
        updated = syncLineAcceptQty(updated, sanitizeQtyInput(val));
      }
      if (key === 'accept_free_quantity') {
        updated = syncLineAcceptFreeQty(updated, sanitizeQtyInput(val));
      }
      return updated;
    }));
  }

  function updateDirectLine(index, key, val, item) {
    setLines(prev => prev.map((row, idx) => {
      if (idx !== index) return row;
      let updated = { ...row, [key]: val };
      if (key === 'item_id' && item) {
        const uomId = defaultUom(item, 'purchase') || row.uom_id || '';
        updated.uom_id = uomId;
        updated.uom_name = item.purchase_uom_name || uomMap[uomId] || item.base_uom_name || item.uom_name || '';
        updated.uom_code = item.purchase_uom_code || uomMap[uomId] || item.base_uom_code || item.uom_code || '';
        updated.item_name = itemOptionLabel(item);
        if (!updated.unit_rate && item.default_purchase_rate) {
          updated.unit_rate = String(item.default_purchase_rate);
        }
        if (!updated.mrp && item.mrp) updated.mrp = String(item.mrp);
        if (!updated.sale_rate && item.sale_rate) updated.sale_rate = String(item.sale_rate);
        // Item master's tax field is `gst_rate` (Tax Master-derived), not `tax_percent`.
        if (item.gst_rate != null && !Number(updated.tax_percent)) {
          updated.tax_percent = String(item.gst_rate);
        }
        updated.is_batch_tracked = Boolean(item.is_batch_tracked);
        updated.is_expiry_tracked = Boolean(item.is_expiry_tracked);
        updated.item_code = item.item_code || '';
        // Kept on the line so the pack-size policy doesn't depend on the shared
        // catalog, which only caches the first page of items. The dispense unit
        // rides along with the base unit: the pack size is typed against the
        // first and posted against the second.
        updated.base_uom_id = item.base_uom_id || '';
        updated.issue_uom_id = defaultUom(item, 'issue') || '';
      }
      if (key === 'uom_id') {
        // Re-resolve the label from the same global list the picker's options
        // came from, and drop the pack size with it: "1 BOX = 15 STR" says
        // nothing once the line is received in CTN. Clearing the master default
        // too is what lets the effect below reseed from the new unit.
        updated.uom_name = uomMap[val] || '';
        updated.uom_code = uomMap[val] || '';
        updated.pack_to_issue = '';
        updated.pack_to_issue_default = '';
        updated.override_reason = '';
      }
      if (key === 'received_quantity') {
        updated = syncLineReceivedQty(updated, sanitizeQtyInput(val));
      } else if (key === 'unit_rate' || key === 'discount_percent') {
        const received = Math.floor(Number(updated.received_quantity) || 0);
        updated.discount_amount = String(
          (received * (Number(updated.unit_rate) || 0) * (Number(updated.discount_percent) || 0)) / 100,
        );
      }
      if (key === 'accept_quantity') {
        updated = syncLineAcceptQty(updated, sanitizeQtyInput(val));
      }
      if (key === 'accept_free_quantity') {
        updated = syncLineAcceptFreeQty(updated, sanitizeQtyInput(val));
      }
      return catalog ? enrichLineWithItemTracking(updated, catalog) : updated;
    }));
  }

  function clearLineErrorOverride(index, field) {
    setLineErrorOverrides((prev) => {
      if (!prev[index]?.[field]) return prev;
      const next = { ...prev, [index]: { ...prev[index] } };
      delete next[index][field];
      if (!Object.keys(next[index]).length) delete next[index];
      return next;
    });
  }

  function handleGrnLineUpdate(index, key, val, itemOrPo) {
    if (key === 'batch_number' || key === 'expiry_date') {
      clearLineErrorOverride(index, key);
      setSubmitLineError(null);
    }
    const dateKeys = ['expiry_date'];
    // Normalize calendar keyboard input to yyyy-MM-dd so the expiry compare in
    // validateLineDates cannot be bypassed by a dd/mm/yyyy string.
    const normalizedVal = dateKeys.includes(key)
      ? (normalizeToIsoDate(val) || val)
      : (['received_quantity', 'free_quantity'].includes(key) ? sanitizeQtyInput(val) : val);
    if (isDirect) {
      if (key === 'item_id') {
        updateDirectLine(index, 'item_id', val, itemOrPo);
      } else {
        updateDirectLine(index, key, normalizedVal);
      }
      return;
    }
    updatePoLine(index, key, normalizedVal);
  }

  function handlePoItemSelect(index, poItem) {
    updatePoLine(index, 'po_item', null, poItem);
  }

  function resolveLineErrors(line, index) {
    const base = isDirect ? getDirectLineValidationErrors(line) : getPoLineValidationErrors(line);
    const overrides = lineErrorOverrides[index];
    return overrides ? { ...base, ...overrides } : base;
  }

  function updateLine(i, key, val) {
    setLines(l => l.map((row, idx) => {
      if (idx !== i) return row;
      if (key === 'received_quantity') {
        return syncLineReceivedQty({ ...row, [key]: val }, sanitizeQtyInput(val));
      }
      if (key === 'accept_quantity') {
        return syncLineAcceptQty(row, sanitizeQtyInput(val));
      }
      if (key === 'accept_free_quantity') {
        return syncLineAcceptFreeQty(row, sanitizeQtyInput(val));
      }
      if (key === 'reject_quantity' || key === 'reject_free_quantity') return row;
      return { ...row, [key]: val };
    }));
  }

  function validateAllLines() {
    if (isDirect) return validateDirectGrnLines(lines);
    return validatePoGrnLines(lines);
  }

  function validateAllQc() {
    for (const line of lines) {
      const err = validateWizardLineQc(line);
      if (err) return err;
    }
    return null;
  }

  function proceedToReview() {
    const qcErr = validateAllQc();
    if (qcErr) {
      toast.error(qcErr);
      return;
    }
    setStep(4);
  }

  async function doCreateGrn() {
    if (grnId) {
      setStep(5);
      toast.info(
        `GRN ${grnData.grn_number} was already created (${grnStatus || 'DRAFT'}). `
        + (qcCompleted ? 'Continue to Post to Stock.' : 'Complete quality check, then post to stock.'),
      );
      return;
    }

    const lineErr = validateGrnLinesForCreate(lines, { isDirect });
    if (lineErr) {
      setSubmitLineError(lineErr);
      toast.error(lineErr);
      setStep(2);
      return;
    }

    setSubmitLineError(null);
    setLineErrorOverrides({});
    setLoading(true);
    setCreateProgress('Starting…');
    try {
      const onProgress = msg => setCreateProgress(msg);
      const onGrnCreated = ({ grn }) => rememberGrnFromResponse(grn);
      const result = isDirect
        ? await submitDirectGrnReceipt({
          grnData,
          lines,
          scope: directScope(),
          onProgress,
          onGrnCreated,
        })
        : await submitPoGrnReceipt({
          grnData,
          purchaseOrderId: selectedPoId,
          selectedPo,
          lines,
          scope: poScope(),
          onProgress,
          onGrnCreated,
        });

      rememberGrnFromResponse(result.grn);
      setGrnItems(result.grnItems);
      setGrnScope(result.scope);
      setGrnStatus(result.status || normalizeGrnStatus(result.grn.status) || 'DRAFT');
      setLines(prev => prev.map(line => ({
        ...line,
        item_name: meaningfulItemName(line.item_name, line.item_id),
      })));

      toast.success(`GRN created (${result.status || 'DRAFT'})`);
      setStep(5);
    } catch (e) {
      const msg = e?.message || 'Failed to create GRN';
      const parsed = parseGrnApiLineError(msg, lines, catalog);
      if (parsed) {
        setLineErrorOverrides(prev => ({
          ...prev,
          [parsed.lineIndex]: { ...prev[parsed.lineIndex], [parsed.field]: parsed.message },
        }));
        setSubmitLineError(msg);
        setStep(2);
      }
      toast.error(msg);
    } finally {
      setLoading(false);
      setCreateProgress('');
    }
  }

  async function doCompleteQc() {
    if (!can(INV_ACTIONS.GRN_QC_ENTRY)) return;
    if (!grnId) {
      toast.error('GRN not found — create the GRN first');
      return;
    }

    const qcErr = validateAllQc();
    if (qcErr) {
      toast.error(qcErr);
      return;
    }

    setLoading(true);
    setCreateProgress('Starting…');
    try {
      const result = await applyGrnQualityCheck({
        grnId,
        grnItems,
        lines,
        scope: workflowScope(),
        grnData,
        grnStatus,
        onProgress: setCreateProgress,
      });
      setGrnItems(result.items);
      setGrnStatus(result.status);
      setGrnScope(result.scope);
      rememberGrnFromResponse(result.grn);
      toast.success('Quality check completed');
    } catch (e) {
      toast.error(e?.message || 'Failed to complete quality check');
    } finally {
      setLoading(false);
      setCreateProgress('');
    }
  }

  async function doPostToStock() {
    // Role gate, not just a render gate: the wizard is deep-linkable via
    // ?grn_id=, and a QC_COMPLETED GRN resumes straight onto this step.
    if (!can(INV_ACTIONS.GRN_POST_TO_STOCK)) return;
    if (!canPostGrnToStock(grnStatus)) {
      toast.error('Complete quality check before posting to stock');
      return;
    }

    setLoading(true);
    try {
      const result = await postToStock(grnId, {}, workflowScope());
      setPostResult(result);
      const postingStatus = normalizeGrnStatus(result.posting_status || result.status);
      if (postingStatus === 'POSTED' || postingStatus === 'POSTED_TO_STOCK') {
        setPosted(true);
        setGrnStatus('POSTED_TO_STOCK');
        toast.success(result.message || 'Stock posted successfully');
      } else if (postingStatus === 'PARTIALLY_POSTED') {
        toast.warning(result.message || 'Some items failed to post. Review and retry.');
        if (result.item_results?.length) {
          setGrnItems(prev => prev.map((item) => {
            const itemId = item.grn_item_id || item.id;
            const match = result.item_results.find(r =>
              (r.grn_item_id || r.item_id) === itemId);
            return match
              ? { ...item, posting_message: match.message, posting_result_status: match.status }
              : item;
          }));
        }
      } else {
        toast.error(result.message || 'Failed to post to stock');
      }
    } catch (e) {
      toast.error(e?.message || 'Failed to post to stock');
    } finally {
      setLoading(false);
    }
  }

  function resolveStoreName(storeId) {
    const s = stores.find(x => (x.store_id || x.id) === storeId);
    return s ? `${s.store_name} (${s.store_code})` : storeId;
  }

  const finalGrnNumber = postResult?.grn_number || grnData.grn_number;

  function pdfExtras(letterhead) {
    return {
      vendorName: resolveVendorName(grnData.vendor_id),
      storeName: resolveStoreName(grnData.receiving_store_id),
      poNumber: isDirect ? null : selectedPo?.po_number,
      facilityLogo,
      letterhead,
    };
  }

  const revokePdfUrl = useCallback(() => {
    if (pdfUrlRef.current) {
      URL.revokeObjectURL(pdfUrlRef.current);
      pdfUrlRef.current = null;
    }
  }, []);

  const handleGeneratePdf = useCallback(async (letterhead) => {
    if (!grnId) return;
    setPdfLoading(true);
    try {
      revokePdfUrl();
      const pkg = await buildGrnPdfPackage(grnId, workflowScope(), pdfExtras(letterhead));
      pdfUrlRef.current = pkg.url;
      setPdfPackage(pkg);
    } catch (e) {
      toast.error(e.message || 'Failed to generate PDF');
    } finally {
      setPdfLoading(false);
    }
  }, [grnId, selectedPo, grnData, grnScope, isDirect, revokePdfUrl]);

  useEffect(() => {
    if (posted && grnId && !pdfPackage) {
      // Fires automatically once the GRN posts, with nobody to ask — so it uses the
      // letterhead. The button below re-generates either way on demand.
      handleGeneratePdf(true);
    }
  }, [posted, grnId, pdfPackage, handleGeneratePdf]);

  useEffect(() => () => revokePdfUrl(), [revokePdfUrl]);

  function handleViewPdf() {
    if (!pdfPackage?.url) {
      toast.info('Generate the PDF first');
      return;
    }
    openGrnPdfUrl(pdfPackage.url);
  }

  function handleDownloadPdf() {
    if (!pdfPackage?.url) {
      toast.info('Generate the PDF first');
      return;
    }
    downloadGrnPdfUrl(pdfPackage.url, pdfPackage.filename);
  }

  const grnDateToday = todayGrnDate();

  const poOptions = useMemo(() => pos.map(p => ({
    value: p.purchase_order_id || p.id,
    label: `${p.po_number} — ${resolveVendorName(p.vendor_id)}`,
  })), [pos, resolveVendorName]);

  const vendorOptions = useMemo(() => purchasableVendors.map(v => ({
    value: v.vendor_id || v.id,
    label: v.vendor_code ? `${v.vendor_name} (${v.vendor_code})` : v.vendor_name,
  })), [purchasableVendors]);

  const canProceedStep0 = isDirect ? Boolean(grnData.vendor_id) : Boolean(selectedPoId);
  const storeOptions = useMemo(
    () => stores
      .filter(s => canReceiveGrn(s))
      .map(s => ({ value: s.store_id || s.id, label: `${s.store_name} (${s.store_code})` })),
    [stores],
  );

  const storeLocked = myStoreIds.length === 1 && storeOptions.some(o => o.value === myStoreId);

  const linesValid = isGrnWizardLinesValid(lines, isDirect);

  const receivedFinancials = useMemo(
    () => computeGrnFinancials(lines, { lines }),
    [lines],
  );

  const acceptedFinancials = useMemo(
    () => computeGrnFinancials(
      lines.map(l => ({ ...l, received_quantity: l.accept_quantity ?? l.received_quantity })),
      { lines },
    ),
    [lines],
  );

  // Scheme units are rare, so the free columns only appear when the vendor
  // actually sent some — an ordinary GRN keeps the narrower table. grnItems is
  // checked too: resuming an existing GRN lands on Post to Stock with the
  // server's lines, which is exactly where the quantity matters most.
  const hasFreeQty = useMemo(
    () => [...lines, ...grnItems].some(l => Math.floor(Number(l.free_quantity) || 0) > 0),
    [lines, grnItems],
  );

  const hasPostingMessages = useMemo(
    () => grnItems.some(item => item.posting_message),
    [grnItems],
  );

  // A conversion failure is recoverable WITHOUT editing the GRN: posting
  // resolves the item's rules live, so adding the missing rule and retrying
  // this same GRN works. Worth saying out loud — the GRN itself is frozen
  // once QC completes, so the obvious "go back and fix the line" is not an option.
  const postFailedOnConversion = useMemo(() => {
    if (!postResult || posted) return false;
    const texts = [postResult.message, ...grnItems.map(i => i.posting_message)];
    return texts.some(t => /conversion/i.test(t || ''));
  }, [postResult, posted, grnItems]);

  const qtyTotals = useMemo(() => {
    const totalReceived = lines.reduce((s, l) => s + Math.floor(Number(l.received_quantity) || 0), 0);
    const totalAccepted = lines.reduce(
      (s, l) => s + Math.floor(Number(l.accept_quantity ?? l.received_quantity) || 0),
      0,
    );
    const totalAcceptedFree = lines.reduce((s, l) => s + resolveLineAcceptFreeQty(l), 0);
    return {
      totalReceived,
      totalAccepted,
      totalRejected: totalReceived - totalAccepted,
      totalAcceptedFree,
      // The number that actually lands in the stock ledger. Received and
      // Accepted are both billed-only, so neither one answers "how many units
      // am I about to put on the shelf".
      totalToStock: totalAccepted + totalAcceptedFree,
    };
  }, [lines]);

  function lineForGrnItem(item, index) {
    return lines.find(l =>
      l.item_id === item.item_id
      && (l.po_item_id === (item.purchase_order_item_id || item.po_item_id) || !l.po_item_id),
    ) || (index != null ? lines[index] : null);
  }

  // Server accept/reject is only trustworthy once QC has actually been applied;
  // before that the create response reports "0.000", so derive from the wizard line.
  function qcQtysForGrnItem(item, index) {
    const line = lineForGrnItem(item, index);
    const received = Math.floor(Number(line?.received_quantity ?? item.received_quantity) || 0);
    const free = Math.floor(Number(line?.free_quantity ?? item.free_quantity) || 0);
    const qcApplied = Boolean(item.qc_status) && item.qc_status !== 'PENDING';
    if (qcApplied && item.accepted_quantity != null) {
      const accepted = Math.floor(Number(item.accepted_quantity) || 0);
      const rejected = item.rejected_quantity != null
        ? Math.floor(Number(item.rejected_quantity) || 0)
        : Math.max(0, received - accepted);
      // Once QC is saved the server owns the split. A GRN that predates free-qty
      // handling reports 0 here, which is correct — its free units never posted.
      const acceptedFree = Math.floor(Number(item.accepted_free_quantity) || 0);
      return { received, accepted, rejected, free, acceptedFree, toStock: accepted + acceptedFree };
    }
    const accepted = Math.min(
      received,
      Math.floor(Number(line?.accept_quantity ?? item.accepted_quantity ?? received) || 0),
    );
    const acceptedFree = line ? resolveLineAcceptFreeQty(line) : 0;
    return {
      received,
      accepted,
      rejected: Math.max(0, received - accepted),
      free,
      acceptedFree,
      toStock: accepted + acceptedFree,
    };
  }

  const showStepper = !posted;

  return (
    <Box>
      <Typography variant="h5" sx={{ color: IPD_COLORS.textPrimary, fontWeight: 700, mb: 1 }}>
        Goods Receipt Note
      </Typography>
      {isDirect && !posted && step < 5 && (
        <Typography sx={{ color: IPD_COLORS.textSecondary, fontSize: 13, mb: 3 }}>
          Direct receipt mode — items and rates entered manually, no purchase order linked.
        </Typography>
      )}
      {(!isDirect || posted || step >= 5) && <Box sx={{ mb: posted ? 0 : 3 }} />}

      {showStepper && (
        <Stepper
          activeStep={step}
          alternativeLabel={!isCompactStepper}
          orientation={isCompactStepper ? 'vertical' : 'horizontal'}
          sx={{
            mb: 4,
            ...(isCompactStepper ? { px: { xs: 0, sm: 1 } } : null),
            '& .MuiStepLabel-label': { color: IPD_COLORS.textSecondary, fontSize: { xs: 11, sm: 12 } },
            '& .MuiStepLabel-label.Mui-active': { color: IPD_COLORS.primary },
            '& .MuiStepLabel-label.Mui-completed': { color: IPD_COLORS.success.text },
            '& .MuiStepIcon-root': { color: IPD_COLORS.border },
            '& .MuiStepIcon-root.Mui-active': { color: IPD_COLORS.primary },
            '& .MuiStepIcon-root.Mui-completed': { color: IPD_COLORS.success.text },
            '& .MuiStepConnector-line': { borderColor: IPD_COLORS.border },
          }}
        >
          {stepLabels.map(label => <Step key={label}><StepLabel>{label}</StepLabel></Step>)}
        </Stepper>
      )}

      {/* Step 0: Select source */}
      {step === 0 && !posted && (
        <SectionCard title={isDirect ? 'Direct Receipt Setup' : 'Select Purchase Order'}>
          {!isPoLocked && (
            <GrnReceiptModeSelector
              value={grnMode}
              onChange={handleReceiptModeChange}
              disabledModes={isPoLocked ? [GRN_MODES.DIRECT] : []}
            />
          )}

          {isDirect ? (
            <Box sx={{ maxWidth: { xs: '100%', sm: 520 } }}>
              <Box id="grn-field-vendor_id">
                <InvSelect
                  name="vendor_id"
                  label="Vendor *"
                  value={grnData.vendor_id}
                  onChange={e => onVendorSelect(e.target.value)}
                  options={vendorOptions}
                  disabled={vendorsLoading}
                  error={Boolean(formErrors.vendor_id)}
                  helperText={formErrors.vendor_id || (vendorsLoading ? 'Loading vendors…' : 'Select the supplier for this direct receipt')}
                />
              </Box>
              {grnData.vendor_id && (
                <Alert severity="info" sx={{ mt: 2 }}>
                  Direct receipt — no purchase order will be linked. Fill GRN details in the next step.
                </Alert>
              )}
            </Box>
          ) : (
            <Box sx={{ maxWidth: { xs: '100%', sm: 520 } }}>
              <InvSelect label="Purchase Order" value={selectedPoId} onChange={e => onPoSelect(e.target.value)}
                options={poOptions} />
              {selectedPo && (
                <Box sx={{ mt: 2, p: 2, backgroundColor: `${IPD_COLORS.primary}12`, borderRadius: 1.5,
                  border: `1px solid ${IPD_COLORS.primary}33` }}>
                  <Typography sx={{ color: IPD_COLORS.textPrimary, fontSize: 13 }}>PO: <strong>{selectedPo.po_number}</strong></Typography>
                  <Typography sx={{ color: IPD_COLORS.textSecondary, fontSize: 12, mt: 0.5 }}>
                    Vendor: {resolveVendorName(selectedPo.vendor_id)}
                  </Typography>
                  <Typography sx={{ color: IPD_COLORS.textSecondary, fontSize: 12 }}>
                    Expected: {formatDate(selectedPo.expected_delivery_date)}
                  </Typography>
                </Box>
              )}
            </Box>
          )}

          <Box sx={{ mt: 3, display: 'flex', flexWrap: 'wrap', gap: 1 }}>
            <InvButton disabled={!canProceedStep0} onClick={proceedFromSource}>
              Next: GRN Details
            </InvButton>
          </Box>
        </SectionCard>
      )}

      {/* Step 1: GRN Details */}
      {step === 1 && !posted && (
        <SectionCard title="GRN Details">
          <GrnDetailsFields
            grnData={grnData}
            formErrors={formErrors}
            isDirect={isDirect}
            grnDateToday={grnDateToday}
            vendorOptions={vendorOptions}
            vendorsLoading={vendorsLoading}
            storeOptions={storeOptions}
            storeLocked={storeLocked}
            updateGrnField={updateGrnField}
            onVendorSelect={onVendorSelect}
            resolveVendorName={resolveVendorName}
          />
          {!grnData.receiving_store_id && (
            <Alert severity="warning" sx={{ mt: 2, maxWidth: { xs: '100%', lg: 960 } }}>
              Please select a store. Stock will post to one store — use Stock Transfer after posting if needed.
            </Alert>
          )}
          <Box sx={{ mt: 3, display: 'flex', flexWrap: 'wrap', gap: 1 }}>
            <InvButton variant="outlined" tone="teal" onClick={() => setStep(0)}>Back</InvButton>
            <InvButton onClick={proceedFromDetails}>
              Next: Add Items
            </InvButton>
          </Box>
        </SectionCard>
      )}

      {/* Step 2: Add Items */}
      {step === 2 && !posted && (
        <SectionCard
          title="Add Received Items"
          actions={(
            <InvButton
              size="small"
              startIcon={<AddIcon />}
              onClick={() => tryAddLine()}
              disabled={!isDirect && (linesLoading || Boolean(linesLoadError))}
            >
              Add Row
            </InvButton>
          )}
        >
          <Typography sx={{ color: IPD_COLORS.textSecondary, mb: 2, fontSize: 13 }}>
            {isDirect
              ? 'Enter item details in two rows per line — quantities and rates on top, batch and pricing below. '
                + "Each line's UOM list is limited to units that convert to that item's base unit; "
                + 'add a rule on the item’s UOM Conversion tab to widen it.'
              : 'Select PO items, then fill received quantities, batch details, MRP, rates, tax, and discounts for each line.'}
          </Typography>

          {linesLoading && (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, py: 4, justifyContent: 'center' }}>
              <CircularProgress size={22} sx={{ color: IPD_COLORS.primary }} />
              <Typography sx={{ color: IPD_COLORS.textSecondary, fontSize: 13 }}>
                Loading purchase order items…
              </Typography>
            </Box>
          )}

          {submitLineError && !linesLoading && (
            <Alert severity="error" sx={{ mb: 2 }} onClose={() => setSubmitLineError(null)}>
              {submitLineError}
            </Alert>
          )}

          {linesLoadError && !linesLoading && (
            <Alert
              severity="error"
              sx={{ mb: 2 }}
              action={(
                <InvButton
                  size="small"
                  tone="teal"
                  onClick={() => {
                    poLinesFetchPoRef.current = null;
                    poLinesAttemptRef.current = null;
                    loadPoItems();
                  }}
                >
                  Retry
                </InvButton>
              )}
            >
              {linesLoadError}
            </Alert>
          )}

          {!linesLoading && (
          <>
          <GrnLineItemsEditor
            lines={lines}
            isDirect={isDirect}
            uomMap={uomMap}
            uomOptions={uomOptions}
            uomPolicy={uomPolicy}
            getPoItemOptions={availablePoItemsForRow}
            getLineErrors={(line, i) => resolveLineErrors(line, i)}
            showValidationErrors={linesValidationAttempted}
            canRemoveLine={lines.length > 1}
            onAddLine={tryAddLine}
            onRemoveLine={i => (isDirect ? removeDirectLine(i) : removePoLine(i))}
            onUpdateLine={handleGrnLineUpdate}
            onPoItemSelect={handlePoItemSelect}
            todayDate={grnDateToday}
            canOverridePricing={canOverrideGrnPricing()}
            receivingStoreId={grnData.receiving_store_id}
          />
          {lines.length > 0 && (
            <GrnFinancialFooter financials={receivedFinancials} useAccepted={false} />
          )}
          </>
          )}

          <Box sx={{ mt: 3, display: 'flex', flexWrap: 'wrap', gap: 1 }}>
            <InvButton
              variant="outlined"
              tone="teal"
              onClick={() => {
                setLinesValidationAttempted(false);
                setSubmitLineError(null);
                setStep(1);
              }}
            >
              Back
            </InvButton>
            <InvButton
              disabled={linesLoading || Boolean(linesLoadError) || !linesValid}
              onClick={proceedFromItems}
            >
              Next: QC Check
            </InvButton>
          </Box>
        </SectionCard>
      )}

      {/* Step 3: QC Check */}
      {step === 3 && !posted && (
        <SectionCard title="Quality Check">
          <Typography sx={{ color: IPD_COLORS.textSecondary, mb: 2, fontSize: 13 }}>
            Review each line and set accepted quantities. Rejected items require a reason.
            {hasFreeQty && ' Free (scheme) units are inspected separately — they are stocked but never billed.'}
          </Typography>
          <Box sx={{ overflowX: 'auto' }}>
            <Table size="small" sx={{ '& td, & th': { borderColor: IPD_COLORS.border, color: IPD_COLORS.textPrimary } }}>
              <TableHead>
                <TableRow>
                  <TableCell sx={{ color: IPD_COLORS.textSecondary }}>Item</TableCell>
                  <TableCell align="right" sx={{ color: IPD_COLORS.textSecondary }}>Received</TableCell>
                  <TableCell sx={{ color: IPD_COLORS.textSecondary, width: 110 }}>Accept Qty</TableCell>
                  <TableCell align="right" sx={{ color: IPD_COLORS.textSecondary, width: 90 }}>Reject</TableCell>
                  {hasFreeQty && (
                    <>
                      <TableCell align="right" sx={{ color: IPD_COLORS.textSecondary, width: 70 }}>Free</TableCell>
                      <TableCell sx={{ color: IPD_COLORS.textSecondary, width: 110 }}>Accept Free</TableCell>
                      <TableCell align="right" sx={{ color: IPD_COLORS.textSecondary, width: 90 }}>Reject Free</TableCell>
                    </>
                  )}
                  <TableCell sx={{ color: IPD_COLORS.textSecondary, minWidth: 180 }}>Rejection Reason</TableCell>
                  <TableCell sx={{ color: IPD_COLORS.textSecondary }}>Batch</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {lines.map((line, i) => {
                  const receivedInt = parseInt(line.received_quantity, 10) || 0;
                  const rejectQty = line.reject_quantity ?? (receivedInt - (line.accept_quantity ?? receivedInt));
                  const freeInt = parseInt(line.free_quantity, 10) || 0;
                  const acceptedInt = Math.floor(Number(line.accept_quantity ?? receivedInt) || 0);
                  // Resolved, not raw: what the row shows must be what the QC
                  // call sends, including the fully-rejected-lot rule.
                  const acceptFreeQty = resolveLineAcceptFreeQty(line);
                  const rejectFreeQty = freeInt - acceptFreeQty;
                  return (
                    <TableRow key={i}>
                      <TableCell>{line.item_name}</TableCell>
                      <TableCell align="right">{formatQty(receivedInt)}</TableCell>
                      <TableCell sx={{ py: 1.25, verticalAlign: 'middle' }}>
                        <InvTextField
                          value={line.accept_quantity ?? receivedInt}
                          label=""
                          onChange={e => (isDirect
                            ? updateDirectLine(i, 'accept_quantity', sanitizeQtyInput(e.target.value))
                            : updateLine(i, 'accept_quantity', sanitizeQtyInput(e.target.value)))}
                          type="number"
                          inputProps={{ min: 0, max: receivedInt, step: 1, inputMode: 'numeric' }}
                          disabled={!receivedInt}
                        />
                      </TableCell>
                      <TableCell align="right">
                        <Typography sx={{
                          color: rejectQty > 0 ? IPD_COLORS.error.text : IPD_COLORS.textSecondary,
                          fontSize: 13,
                        }}>
                          {formatQty(rejectQty)}
                        </Typography>
                      </TableCell>
                      {hasFreeQty && (
                        <>
                          <TableCell align="right">
                            <Typography sx={{ color: IPD_COLORS.textSecondary, fontSize: 13 }}>
                              {freeInt ? formatQty(freeInt) : '—'}
                            </Typography>
                          </TableCell>
                          <TableCell sx={{ py: 1.25, verticalAlign: 'middle' }}>
                            {freeInt ? (
                              <InvTextField
                                value={acceptFreeQty}
                                label=""
                                onChange={e => (isDirect
                                  ? updateDirectLine(i, 'accept_free_quantity', sanitizeQtyInput(e.target.value))
                                  : updateLine(i, 'accept_free_quantity', sanitizeQtyInput(e.target.value)))}
                                type="number"
                                inputProps={{ min: 0, max: freeInt, step: 1, inputMode: 'numeric' }}
                                // Unpaid units cannot enter stock off a fully
                                // rejected lot — they would be valued at zero.
                                disabled={acceptedInt <= 0}
                              />
                            ) : (
                              <Typography sx={{ color: IPD_COLORS.textSecondary, fontSize: 12 }}>—</Typography>
                            )}
                          </TableCell>
                          <TableCell align="right">
                            <Typography sx={{
                              color: rejectFreeQty > 0 ? IPD_COLORS.error.text : IPD_COLORS.textSecondary,
                              fontSize: 13,
                            }}>
                              {freeInt ? formatQty(rejectFreeQty) : '—'}
                            </Typography>
                          </TableCell>
                        </>
                      )}
                      <TableCell sx={{ py: 1.25, verticalAlign: 'middle' }}>
                        {rejectQty > 0 ? (
                          <InvTextField
                            value={line.rejection_reason || ''}
                            label=""
                            onChange={e => (isDirect
                              ? updateDirectLine(i, 'rejection_reason', e.target.value)
                              : updateLine(i, 'rejection_reason', e.target.value))}
                          />
                        ) : (
                          <Typography sx={{ color: IPD_COLORS.textSecondary, fontSize: 12 }}>—</Typography>
                        )}
                      </TableCell>
                      <TableCell sx={{ fontFamily: 'monospace', fontSize: 12 }}>{line.batch_number || '—'}</TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </Box>
          <GrnFinancialFooter financials={acceptedFinancials} useAccepted />
          <Box sx={{ mt: 3, display: 'flex', flexWrap: 'wrap', gap: 1 }}>
            <InvButton variant="outlined" tone="teal" onClick={() => setStep(2)}>Back</InvButton>
            <InvButton onClick={proceedToReview}>
              Next: Review &amp; Create GRN
            </InvButton>
          </Box>
        </SectionCard>
      )}

      {/* Step 4: Review & Create GRN */}
      {step === 4 && !posted && (
        <SectionCard title="Review & Create GRN">
          <Alert severity="info" sx={{ mb: 3 }}>
            Confirm the summary below. Creating the GRN saves the header and line items as <strong>DRAFT</strong>.
            On the next step, confirm receipt and complete quality check before posting to stock.
          </Alert>

          <Grid container spacing={2} sx={{ mb: 3 }}>
            {[
              { label: 'GRN Number', value: grnData.grn_number },
              { label: 'Vendor', value: resolveVendorName(grnData.vendor_id) },
              { label: 'Store', value: resolveStoreName(grnData.receiving_store_id) },
              { label: 'Invoice', value: `${grnData.invoice_number} (${formatDate(grnData.invoice_date)})` },
              ...(isDirect ? [] : [{ label: 'PO', value: selectedPo?.po_number }]),
            ].filter(r => r.value).map(row => (
              <Grid item xs={12} sm={6} md={4} key={row.label}>
                <Box sx={{ ...cardSx, p: 1.5 }}>
                  <Typography sx={{ color: IPD_COLORS.textSecondary, fontSize: 11 }}>{row.label}</Typography>
                  <Typography sx={{ color: IPD_COLORS.textPrimary, fontWeight: 600, fontSize: 14 }}>{row.value}</Typography>
                </Box>
              </Grid>
            ))}
          </Grid>

          <Grid container spacing={2} sx={{ mb: 3 }}>
            {[
              { label: 'Total Received', value: qtyTotals.totalReceived, tone: 'teal' },
              { label: 'Total Accepted', value: qtyTotals.totalAccepted, tone: 'mint' },
              { label: 'Total Rejected', value: qtyTotals.totalRejected, tone: qtyTotals.totalRejected > 0 ? 'red' : 'teal' },
              // Only meaningful once a scheme is involved — otherwise Free is 0
              // and To Stock just repeats Total Accepted.
              ...(hasFreeQty ? [
                { label: 'Free Accepted', value: qtyTotals.totalAcceptedFree, tone: 'mint' },
                { label: 'Total to Stock', value: qtyTotals.totalToStock, tone: 'mint' },
              ] : []),
            ].map(stat => (
              <Grid item xs={12} sm={4} key={stat.label}>
                <Box sx={{ ...cardSx, p: 2, textAlign: 'center' }}>
                  <Typography sx={{ color: IPD_COLORS.textSecondary, fontSize: 11, mb: 0.5 }}>{stat.label}</Typography>
                  <Typography sx={{
                    fontSize: 24, fontWeight: 700,
                    color: stat.tone === 'mint' ? IPD_COLORS.success.text
                      : stat.tone === 'red' ? IPD_COLORS.error.text : IPD_COLORS.primary,
                  }}>
                    {formatQty(stat.value)}
                  </Typography>
                </Box>
              </Grid>
            ))}
          </Grid>

          <Box sx={{ overflowX: 'auto', mb: 2 }}>
            <Table size="small" sx={{ '& td, & th': { borderColor: IPD_COLORS.border, color: IPD_COLORS.textPrimary } }}>
              <TableHead>
                <TableRow>
                  <TableCell sx={{ color: IPD_COLORS.textSecondary }}>Item</TableCell>
                  <TableCell align="right" sx={{ color: IPD_COLORS.textSecondary }}>Received</TableCell>
                  <TableCell align="right" sx={{ color: IPD_COLORS.textSecondary }}>Accepted</TableCell>
                  <TableCell align="right" sx={{ color: IPD_COLORS.textSecondary }}>Rejected</TableCell>
                  {hasFreeQty && (
                    <>
                      <TableCell align="right" sx={{ color: IPD_COLORS.textSecondary }}>Free Accepted</TableCell>
                      <TableCell align="right" sx={{ color: IPD_COLORS.textSecondary }}>To Stock</TableCell>
                    </>
                  )}
                  <TableCell sx={{ color: IPD_COLORS.textSecondary }}>Batch</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {lines.map((line, i) => {
                  const received = Math.floor(Number(line.received_quantity) || 0);
                  const accepted = Math.floor(Number(line.accept_quantity ?? received) || 0);
                  const acceptedFree = resolveLineAcceptFreeQty(line);
                  return (
                    <TableRow key={i}>
                      <TableCell>{line.item_name}</TableCell>
                      <TableCell align="right">{formatQty(received)}</TableCell>
                      <TableCell align="right" sx={{ color: IPD_COLORS.success.text }}>{formatQty(accepted)}</TableCell>
                      <TableCell align="right" sx={{ color: received - accepted > 0 ? IPD_COLORS.error.text : IPD_COLORS.textSecondary }}>
                        {formatQty(received - accepted)}
                      </TableCell>
                      {hasFreeQty && (
                        <>
                          <TableCell align="right" sx={{ color: IPD_COLORS.textSecondary }}>
                            {acceptedFree > 0 ? formatQty(acceptedFree) : '—'}
                          </TableCell>
                          <TableCell align="right" sx={{ color: IPD_COLORS.success.text, fontWeight: 600 }}>
                            {formatQty(accepted + acceptedFree)}
                          </TableCell>
                        </>
                      )}
                      <TableCell sx={{ fontFamily: 'monospace', fontSize: 12 }}>{line.batch_number || '—'}</TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </Box>

          <GrnFinancialFooter financials={acceptedFinancials} useAccepted />

          {createProgress && (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mt: 2 }}>
              <CircularProgress size={18} sx={{ color: IPD_COLORS.primary }} />
              <Typography sx={{ color: IPD_COLORS.textSecondary, fontSize: 13 }}>{createProgress}</Typography>
            </Box>
          )}

          <Box sx={{ mt: 3, display: 'flex', flexWrap: 'wrap', gap: 1 }}>
            <InvButton variant="outlined" tone="teal" disabled={loading} onClick={() => setStep(3)}>Back</InvButton>
            <InvButton loading={loading} tone="mint" onClick={doCreateGrn}>
              {grnId ? 'Continue to Post to Stock' : 'Create GRN'}
            </InvButton>
          </Box>
        </SectionCard>
      )}

      {/* Step 5: Post to Stock */}
      {step === 5 && !posted && (
        <SectionCard title="Post to Stock">
          {qcCompleted ? (
            <Alert severity="success" sx={{ mb: 2 }}>
              Quality check completed for GRN <strong>{grnData.grn_number}</strong> ({grnStatus}).
              Post accepted quantities to the stock ledger.
            </Alert>
          ) : (
            <Alert severity="warning" sx={{ mb: 2 }}>
              GRN <strong>{grnData.grn_number}</strong> is <strong>{grnStatus || 'DRAFT'}</strong>.
              Confirm receipt and complete quality check before posting to stock.
            </Alert>
          )}
          {postResult && !posted && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {postResult.message || 'Failed to post to stock.'}
              {hasPostingMessages && ' See the Status column below for the specific reason per item.'}
              {postFailedOnConversion && (
                <Typography component="span" sx={{ display: 'block', fontSize: 12, mt: 0.75 }}>
                  This GRN does not need to be re-entered. Add the missing rule on the
                  item&apos;s UOM Conversion tab, then press Post to Stock again — the
                  conversion is resolved fresh each time you post.
                </Typography>
              )}
            </Alert>
          )}
          <Typography sx={{ color: IPD_COLORS.textSecondary, mb: 2, fontSize: 13 }}>
            Review accepted quantities below, then post to stock or complete quality check if needed.
            {hasFreeQty && ' "To Stock" includes accepted free units — that is the quantity the ledger will receive.'}
          </Typography>

          <Box sx={{ mb: 3, overflowX: 'auto' }}>
            <Table size="small" sx={{ '& td, & th': { borderColor: IPD_COLORS.border, color: IPD_COLORS.textPrimary } }}>
              <TableHead>
                <TableRow>
                  <TableCell sx={{ color: IPD_COLORS.textSecondary }}>Item</TableCell>
                  <TableCell align="right" sx={{ color: IPD_COLORS.textSecondary }}>Received</TableCell>
                  <TableCell align="right" sx={{ color: IPD_COLORS.textSecondary }}>Accepted</TableCell>
                  <TableCell align="right" sx={{ color: IPD_COLORS.textSecondary }}>Rejected</TableCell>
                  {hasFreeQty && (
                    <>
                      <TableCell align="right" sx={{ color: IPD_COLORS.textSecondary }}>Free Accepted</TableCell>
                      <TableCell align="right" sx={{ color: IPD_COLORS.textSecondary }}>To Stock</TableCell>
                    </>
                  )}
                  <TableCell sx={{ color: IPD_COLORS.textSecondary }}>Batch</TableCell>
                  {hasPostingMessages && (
                    <TableCell sx={{ color: IPD_COLORS.textSecondary }}>Status</TableCell>
                  )}
                </TableRow>
              </TableHead>
              <TableBody>
                {(grnItems.length ? grnItems : lines).map((item, i) => {
                  const name = item.item_name || displayItemName(item, catalog, lines);
                  const line = lines[i];
                  const { received, accepted, rejected, acceptedFree, toStock } = qcQtysForGrnItem(item, i);
                  const batch = item.batch_number || line?.batch_number;
                  const key = item.grn_item_id || item.id || i;
                  return (
                    <TableRow key={key}>
                      <TableCell>{name}</TableCell>
                      <TableCell align="right">{formatQty(received)}</TableCell>
                      <TableCell align="right" sx={{ color: IPD_COLORS.success.text, fontWeight: 600 }}>
                        {formatQty(accepted)}
                      </TableCell>
                      <TableCell align="right" sx={{
                        color: rejected > 0 ? IPD_COLORS.error.text : IPD_COLORS.textSecondary,
                      }}>
                        {formatQty(rejected)}
                      </TableCell>
                      {hasFreeQty && (
                        <>
                          <TableCell align="right" sx={{ color: IPD_COLORS.textSecondary }}>
                            {acceptedFree > 0 ? formatQty(acceptedFree) : '—'}
                          </TableCell>
                          <TableCell align="right" sx={{ color: IPD_COLORS.success.text, fontWeight: 700 }}>
                            {formatQty(toStock)}
                          </TableCell>
                        </>
                      )}
                      <TableCell sx={{ fontFamily: 'monospace', fontSize: 12 }}>{batch || '—'}</TableCell>
                      {hasPostingMessages && (
                        <TableCell sx={{ fontSize: 12, maxWidth: 260 }}>
                          {item.posting_message ? (
                            <Typography sx={{
                              fontSize: 12,
                              color: item.posting_result_status === 'FAILED'
                                ? IPD_COLORS.error.text
                                : IPD_COLORS.textSecondary,
                            }}>
                              {item.posting_message}
                            </Typography>
                          ) : '—'}
                        </TableCell>
                      )}
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </Box>

          <GrnFinancialFooter financials={acceptedFinancials} useAccepted />

          {createProgress && (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mt: 2 }}>
              <CircularProgress size={18} sx={{ color: IPD_COLORS.primary }} />
              <Typography sx={{ color: IPD_COLORS.textSecondary, fontSize: 13 }}>{createProgress}</Typography>
            </Box>
          )}

          {qcCompleted && !can(INV_ACTIONS.GRN_POST_TO_STOCK) && (
            <Alert severity="info" sx={{ mt: 2 }}>
              Quality check is complete. A Store Manager must post this GRN to stock.
            </Alert>
          )}

          <Box sx={{ mt: 2, display: 'flex', flexWrap: 'wrap', gap: 1 }}>
            {!qcCompleted && can(INV_ACTIONS.GRN_QC_ENTRY) && (
              <InvButton
                size="large"
                loading={loading}
                tone="teal"
                onClick={doCompleteQc}
                sx={{ px: 4, py: 1.5 }}
              >
                Confirm Receipt &amp; Complete QC
              </InvButton>
            )}
            {can(INV_ACTIONS.GRN_POST_TO_STOCK) && (
              <InvButton
                size="large"
                loading={loading}
                tone="mint"
                disabled={!qcCompleted}
                onClick={doPostToStock}
                sx={{ px: 4, py: 1.5 }}
              >
                Post to Stock
              </InvButton>
            )}
          </Box>
        </SectionCard>
      )}

      {/* Success */}
      {posted && (
        <SectionCard title="">
          <Box sx={{ textAlign: 'center', py: 3 }}>
            <CheckCircleIcon sx={{ color: IPD_COLORS.success.text, fontSize: 72, mb: 2 }} />
            <Typography variant="h5" sx={{ color: IPD_COLORS.textPrimary, fontWeight: 700, mb: 1 }}>
              Stock Posted Successfully!
            </Typography>
            <Typography sx={{ color: IPD_COLORS.textSecondary, mb: 1 }}>
              GRN <strong>{finalGrnNumber}</strong> has been posted to stock.
            </Typography>
            <Typography sx={{ color: IPD_COLORS.textSecondary, mb: 3, fontSize: 13 }}>
              Store: {resolveStoreName(grnData.receiving_store_id)}
            </Typography>

            <Box sx={{ display: 'flex', gap: 1.5, justifyContent: 'center', flexWrap: 'wrap', mb: 3 }}>
              <InvButton startIcon={<PictureAsPdfIcon />} loading={pdfLoading} onClick={(e) => letterheadMenu.open(e, handleGeneratePdf)}>
                {pdfPackage ? 'Regenerate PDF' : 'Generate PDF'}
              </InvButton>
              <InvButton startIcon={<VisibilityIcon />} variant="outlined" tone="teal"
                disabled={!pdfPackage || pdfLoading} onClick={handleViewPdf}>
                View PDF
              </InvButton>
              <InvButton startIcon={<DownloadIcon />} variant="outlined" tone="mint"
                disabled={!pdfPackage || pdfLoading} onClick={handleDownloadPdf}>
                Download PDF
              </InvButton>
              <LetterheadMenu menu={letterheadMenu} label="Goods Receipt Note" />
            </Box>

            {pdfLoading && (
              <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', gap: 1, mb: 2 }}>
                <CircularProgress size={20} sx={{ color: IPD_COLORS.primary }} />
                <Typography sx={{ color: IPD_COLORS.textSecondary, fontSize: 13 }}>Generating PDF…</Typography>
              </Box>
            )}

            {pdfPackage?.url && !pdfLoading && (
              <Box sx={{
                mt: 2, mx: 'auto', maxWidth: 900, textAlign: 'left',
                border: `1px solid ${IPD_COLORS.border}`, borderRadius: 2, overflow: 'hidden',
                backgroundColor: '#f8fafb',
              }}>
                <Typography sx={{
                  px: 2, py: 1, fontSize: 12, fontWeight: 600,
                  color: IPD_COLORS.textSecondary, borderBottom: `1px solid ${IPD_COLORS.border}`,
                  backgroundColor: IPD_COLORS.selectedBg,
                }}>
                  GRN PDF Preview — {pdfPackage.filename}
                </Typography>
                <Box
                  component="iframe"
                  src={`${pdfPackage.url}#zoom=page-width`}
                  title="GRN PDF Preview"
                  sx={{ width: '100%', height: { xs: 360, sm: 520 }, border: 'none', display: 'block' }}
                />
              </Box>
            )}

            <Box sx={{ display: 'flex', gap: 2, justifyContent: 'center', flexWrap: 'wrap', mt: 3 }}>
              <InvButton variant="outlined" tone="teal"
                onClick={() => history.push(`${materialBase}/grns`)}>
                View All GRNs
              </InvButton>
              <InvButton variant="outlined" tone="teal"
                onClick={() => history.push(`${materialBase.replace('/material', '/stock/balances')}?store_id=${grnData.receiving_store_id}`)}>
                View Stock
              </InvButton>
              <InvButton onClick={() => history.push(`${materialBase.replace('/material', '/stock')}/transfer/new`)}>
                Create Transfer
              </InvButton>
            </Box>
          </Box>
        </SectionCard>
      )}
    </Box>
  );
}
